import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import { Heart, MessageCircle, X } from "lucide-react";
import { ActivityEntityType } from "protogen/common_pb";
import {
  ForumPost,
  ForumComment,
  FetchForumPostResponse,
} from "protogen/forum_service_pb";
import { useFetchPost, useEditCommentLike } from "services/forum";
import DateDisplay from "../common/DateDisplay";
import { useAdvisorProfilePath, UserAvatar } from "../common/CurrentUserAvatar";
import DocumentViewer from "../editor/DocumentViewer";
import AddComment, { CommentHandle } from "./AddComment";
import LikerTooltip from "./LikerTooltip";
import Loading from "../common/Loading";
import LinkRouter from "components/navigation/LinkRouter";

type MiniCommentProps = {
  comment: ForumComment;
  post: ForumPost;
  depth?: number;
  onCommentCreation: () => void;
  childComments: ForumComment[];
};

const MiniComment = ({
  comment,
  post,
  depth = 0,
  onCommentCreation,
  childComments,
}: MiniCommentProps) => {
  const MAX_DEPTH = 1;
  const getAdvisorProfileUrl = useAdvisorProfilePath();
  const [isLiked, setLiked] = useState(comment.isLikedByUser || false);
  const [replyMode, setReplyMode] = useState(false);
  const [localComment, setLocalComment] = useState(comment);

  const { request: likeRequest } = useEditCommentLike((r) => {
    if (r.isLiked === undefined) return;
    setLiked(r.isLiked);
    setLocalComment((prev) => {
      const updated = prev.clone();
      updated.totalLikes = r.isLiked
        ? prev.totalLikes + 1
        : prev.totalLikes - 1;
      return updated;
    });
  });

  const handleLike = () => {
    likeRequest({
      ref: comment.ref,
      postRef: comment.postRef,
      isLiked: !isLiked,
    });
  };

  const bodyContent = localComment.body?.payload
    ? JSON.parse(localComment.body.payload)
    : localComment.body?.textContent;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        paddingLeft: depth > 0 ? "24px" : 0,
        borderLeft: depth > 0 ? "2px solid #E0E0E0" : "none",
        marginLeft: depth > 0 ? "12px" : 0,
      }}
    >
      <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
        <UserAvatar user={localComment.author!} size={28} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "wrap",
            }}
          >
            <LinkRouter
              to={getAdvisorProfileUrl(localComment.author?.ref || "")}
            >
              <Typography variant="bodySmallHeavy">
                {localComment.author?.displayName}
              </Typography>
            </LinkRouter>
            <DateDisplay
              date={new Date(Number(localComment.createdSec) * 1000)}
              sx={{ fontSize: "11px", color: "#9E9E9E" }}
            />
          </Box>
          <Box sx={{ marginTop: "4px" }}>
            <DocumentViewer
              content={bodyContent}
              attachments={localComment.body?.attachments}
              minHeight="16px"
              sx={{
                fontSize: "13px",
                "& p": { margin: 0, fontSize: "13px" },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              marginTop: "4px",
              alignItems: "center",
            }}
          >
            <LikerTooltip
              likers={comment.likerHints}
              total={localComment.totalLikes}
            >
              <Box
                onClick={handleLike}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <Heart
                  size={14}
                  fill={isLiked ? "#EF7B77" : "none"}
                  stroke={isLiked ? "#EF7B77" : "#9E9E9E"}
                />
                {localComment.totalLikes > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      color: isLiked ? "#EF7B77" : "#9E9E9E",
                    }}
                  >
                    {localComment.totalLikes}
                  </Typography>
                )}
              </Box>
            </LikerTooltip>
            {depth < MAX_DEPTH && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "11px",
                  color: "#9E9E9E",
                  cursor: "pointer",
                  "&:hover": { color: "#616161" },
                }}
                onClick={() => setReplyMode(!replyMode)}
              >
                Reply
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {replyMode && (
        <Box sx={{ marginLeft: "36px" }}>
          <AddComment
            postRef={post.ref}
            parentRef={comment.ref}
            onCreate={() => {
              onCommentCreation();
              setReplyMode(false);
            }}
          />
        </Box>
      )}

      {childComments.map((child) => (
        <MiniComment
          key={child.ref}
          comment={child}
          post={post}
          depth={depth + 1}
          onCommentCreation={onCommentCreation}
          childComments={[]}
        />
      ))}
    </Box>
  );
};

type MiniFeedProps = {
  entityType: ActivityEntityType;
  entityRef: string;
  onClose?: () => void;
  hideHeader?: boolean;
  onCommentsLoaded?: (count: number) => void;
};

export default function MiniFeed({
  entityType,
  entityRef,
  onClose,
  hideHeader = false,
  onCommentsLoaded,
}: MiniFeedProps) {
  const [data, setData] = useState<FetchForumPostResponse | null>(null);
  const commentBoxRef = useRef<CommentHandle>(null);

  const { request, loading, errorCode } = useFetchPost((r) => {
    setData(r);
  });

  const fetchData = useCallback(() => {
    request({
      postRef: "",
      entityType,
      entityRef,
      markAsRead: true,
      getOrCreate: true,
    });
  }, [entityType, entityRef, request]);

  useEffect(() => {
    fetchData();
  }, [entityType, entityRef]);

  const onCommentCreation = () => {
    fetchData();
  };

  const createCommentMap = (
    comments: ForumComment[],
  ): { [key: string]: ForumComment[] } => {
    const commentMap: { [key: string]: ForumComment[] } = {};
    comments.forEach((comment) => {
      commentMap[comment.ref] = [];
    });
    comments.forEach((comment) => {
      if (comment.parentRef !== "" && commentMap[comment.parentRef]) {
        commentMap[comment.parentRef].push(comment);
      }
    });
    return commentMap;
  };

  useEffect(() => {
    if (onCommentsLoaded) {
      onCommentsLoaded(data?.comments?.length || 0);
    }
  }, [data?.comments?.length, onCommentsLoaded]);

  if (errorCode === 410) {
    return (
      <Box
        sx={{
          width: "100%",
          padding: "24px 16px",
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Comments are no longer available because this document was deleted.
        </Typography>
      </Box>
    );
  }

  if (loading || !data || !data.post) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </Box>
    );
  }
  const post = data?.post;
  const comments = data?.comments || [];
  const commentMap = createCommentMap(comments);
  const topLevelComments = comments.filter((c) => c.parentRef === "");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        backgroundColor: hideHeader ? "transparent" : "#FAFAFA",
        borderRadius: hideHeader ? 0 : "12px",
        border: hideHeader ? "none" : "1px solid #E0E0E0",
      }}
    >
      {!hideHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MessageCircle size={18} stroke="#616161" />
            <Typography variant="bodyHeavy" sx={{ fontSize: "14px" }}>
              Comments ({comments.length})
            </Typography>
          </Box>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <X size={18} />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "12px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {topLevelComments.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#9E9E9E", textAlign: "center", padding: "24px 0" }}
          >
            No comments yet. Be the first to comment!
          </Typography>
        ) : (
          topLevelComments.map((comment) => (
            <MiniComment
              key={comment.ref}
              comment={comment}
              post={post!}
              onCommentCreation={onCommentCreation}
              childComments={commentMap[comment.ref] || []}
            />
          ))
        )}
      </Box>

      <Divider />
      <Box sx={{ padding: "8px 16px", overflow: "hidden" }}>
        <AddComment
          postRef={post!.ref}
          onCreate={onCommentCreation}
          ref={commentBoxRef}
        />
      </Box>
    </Box>
  );
}
